import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Receipt, Send, Settings, RefreshCw, Eye, X, Plus, Trash2, Download, Printer,
  AlertCircle, CheckCircle2, Clock, ChevronDown, Building2, Euro,
} from 'lucide-react';
import { FE, Clienti } from '../services/endpoints';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { ListSkeleton, EmptyState } from '../components/ui';
import { generateFatturaPDF } from '../utils/fatturaPDF';

const formatEuro = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v || 0);

// Scarica una stringa come file (l'XML della fattura). EdenFlow è servito da nginx
// (app normale, non Artifact): il download via Blob+anchor funziona senza restrizioni.
function downloadFile(text, filename) {
  const blob = new Blob([text], { type: 'application/xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const REGIMI = [
  ['RF01', 'Ordinario'], ['RF19', 'Forfettario (L.190/2014)'],
  ['RF02', 'Minimi'], ['RF04', 'Agricoltura'], ['RF18', 'Altro'],
];

// Stato locale del documento → colore + etichetta
const STATI = {
  bozza:              { l: 'Bozza',              c: 'var(--text-muted)', bg: 'var(--bg3)' },
  inviata:            { l: 'Inviata a SDI',      c: 'var(--cyan)',       bg: 'var(--cyan-dim)' },
  consegnata:         { l: 'Consegnata',         c: 'var(--green)',      bg: 'var(--green-dim)' },
  accettata:          { l: 'Accettata',          c: 'var(--green)',      bg: 'var(--green-dim)' },
  presa_in_carico:    { l: 'Presa in carico',    c: 'var(--cyan)',       bg: 'var(--cyan-dim)' },
  non_consegnata:     { l: 'Non consegnata',     c: 'var(--yellow)',     bg: 'var(--yellow-dim)' },
  mancata_consegna:   { l: 'Mancata consegna',   c: 'var(--yellow)',     bg: 'var(--yellow-dim)' },
  decorrenza_termini: { l: 'Decorrenza termini', c: 'var(--green)',      bg: 'var(--green-dim)' },
  scartata:           { l: 'Scartata',           c: 'var(--red)',        bg: 'var(--red-dim)' },
  rifiutata:          { l: 'Rifiutata',          c: 'var(--red)',        bg: 'var(--red-dim)' },
  errore:             { l: 'Errore invio',       c: 'var(--red)',        bg: 'var(--red-dim)' },
};
const statoInfo = (s) => STATI[s] || { l: s, c: 'var(--text-muted)', bg: 'var(--bg3)' };
const isBozza = (s) => s === 'bozza';
const puoInviare = (s) => ['bozza', 'scartata', 'errore'].includes(s);

const Label = ({ children }) => (
  <label style={{ display: 'block', fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>{children}</label>
);
const Field = ({ label, children }) => (<div><Label>{label}</Label>{children}</div>);

const CLIENTE_FISCAL = [
  ['piva', 'P.IVA'], ['codice_fiscale', 'Codice Fiscale'],
  ['codice_destinatario', 'Codice Destinatario SDI'], ['pec', 'PEC'],
  ['indirizzo_via', 'Indirizzo'], ['indirizzo_cap', 'CAP'],
  ['indirizzo_comune', 'Comune'], ['indirizzo_provincia', 'Provincia'],
];

export default function FatturaElettronica() {
  const { user } = useAuth();
  const { success } = useToast();

  const [loading, setLoading] = useState(true);
  const [em, setEm] = useState(null);
  const [arubaOk, setArubaOk] = useState(false);
  const [docs, setDocs] = useState([]);
  const [stats, setStats] = useState(null);
  const [clientiList, setClientiList] = useState([]);

  const [editEm, setEditEm] = useState(false);
  const [emForm, setEmForm] = useState({});
  const [savingEm, setSavingEm] = useState(false);

  const [showNew, setShowNew] = useState(false);
  const [xmlModal, setXmlModal] = useState(null); // {id, text}
  const [busyId, setBusyId] = useState(null);
  const [verificando, setVerificando] = useState(false);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      FE.emittente(), FE.list(), FE.stats(), Clienti.attivi(),
    ]).then(([e, l, s, c]) => {
      setEm(e.data);
      setArubaOk(!!e.data.aruba_configurato);
      setEmForm(e.data);
      setDocs(l.data || []);
      setStats(s.data || null);
      setClientiList(c.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveEmittente = async () => {
    setSavingEm(true);
    try {
      const payload = { ...emForm };
      delete payload.campi_mancanti; delete payload.aruba_configurato;
      delete payload.id; delete payload.aggiornato_il;
      const r = await FE.saveEmittente(payload);
      setEm(prev => ({ ...prev, ...r.data }));
      setEditEm(false);
      success('Dati emittente salvati');
    } catch (_) {}
    setSavingEm(false);
  };

  const reloadDocs = () => Promise.all([FE.list(), FE.stats()]).then(([l, s]) => {
    setDocs(l.data || []); setStats(s.data || null);
  });

  const reloadEmittente = () => FE.emittente().then(e => {
    setEm(e.data); setArubaOk(!!e.data.aruba_configurato);
  }).catch(() => {});

  const verificaServizio = async () => {
    setVerificando(true);
    try {
      const r = await FE.verificaServizio();
      await reloadEmittente();
      success(r.data.attivo ? 'Servizio Aruba attivo' : `Servizio Aruba: ${r.data.stato}`);
    } catch (_) {} finally { setVerificando(false); }
  };

  const inviaDoc = async (d) => {
    if (!window.confirm(
      `Inviare la fattura n. ${d.numero}/${d.anno} a SDI?\n\n` +
      `L'invio è IRREVERSIBILE e trasmette il documento tramite Aruba. ` +
      `Verrà firmato e inoltrato al Sistema di Interscambio.`
    )) return;
    setBusyId(d.id);
    try { await FE.invia(d.id); await reloadDocs(); success(`Fattura ${d.numero}/${d.anno} inviata`); }
    catch (_) { reloadEmittente(); await reloadDocs(); }  // può aver disattivato il servizio
    finally { setBusyId(null); }
  };

  const aggiornaStato = async (d) => {
    setBusyId(d.id);
    try { await FE.aggiornaStato(d.id); await reloadDocs(); success('Stato aggiornato'); }
    catch (_) {} finally { setBusyId(null); }
  };

  const eliminaDoc = async (d) => {
    if (!window.confirm(`Eliminare la bozza n. ${d.numero}/${d.anno}?`)) return;
    setBusyId(d.id);
    try { await FE.remove(d.id); await reloadDocs(); success('Bozza eliminata'); }
    catch (_) {} finally { setBusyId(null); }
  };

  const apriXml = async (d) => {
    try { const r = await FE.xml(d.id); setXmlModal({ id: d.id, nome: d.xml_filename, text: r.data }); }
    catch (_) {}
  };

  const scaricaXml = async (d) => {
    try {
      const r = await FE.xml(d.id);
      downloadFile(r.data, d.xml_filename || `fattura_${d.numero}_${d.anno}.xml`);
    } catch (_) {}
  };

  const scaricaPdf = async (d) => {
    setBusyId(d.id);
    try { const full = await FE.get(d.id); generateFatturaPDF(full.data); }
    catch (_) {} finally { setBusyId(null); }
  };

  const grouped = useMemo(() => {
    const byAnno = {};
    docs.forEach(d => { (byAnno[d.anno] = byAnno[d.anno] || []).push(d); });
    return Object.keys(byAnno).sort((a, b) => b - a).map(anno => ({ anno, items: byAnno[anno] }));
  }, [docs]);

  if (user?.ruolo !== 'admin') {
    return (
      <div style={{ padding: 48, textAlign: 'center', color: 'var(--text-muted)' }}>
        <AlertCircle size={40} style={{ marginBottom: 12, color: 'var(--red)' }} />
        <div>Accesso riservato agli amministratori.</div>
      </div>
    );
  }

  const emMissing = em?.campi_mancanti || [];

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
        <Receipt size={28} color="var(--cyan)" />
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>Fattura Elettronica</h1>
        <span style={{ background: 'var(--cyan-dim)', color: 'var(--cyan)', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>ADMIN</span>
        {em && (
          <span style={{ background: em.ambiente === 'produzione' ? 'var(--red-dim)' : 'var(--yellow-dim)', color: em.ambiente === 'produzione' ? 'var(--red)' : 'var(--yellow)', borderRadius: 6, padding: '2px 10px', fontSize: 11, fontWeight: 700, letterSpacing: '0.06em' }}>
            {em.ambiente === 'produzione' ? 'PRODUZIONE' : 'DEMO'}
          </span>
        )}
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '6px 0 20px' }}>
        Emissione FatturaPA verso SDI tramite Aruba (firma + trasmissione a carico di Aruba).
      </p>

      {/* Banner stato Aruba */}
      {!arubaOk ? (
        <div className="eden-card" style={{ padding: '14px 18px', marginBottom: 18, borderLeft: '3px solid var(--yellow)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertCircle size={18} color="var(--yellow)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--text)' }}>
            <b>Invio a SDI non ancora attivo.</b> Puoi creare bozze, generare e scaricare l'XML.
            Per abilitare l'invio servono le credenziali del servizio Aruba Fatturazione Elettronica
            (<code>ARUBA_FE_USERNAME</code> / <code>ARUBA_FE_PASSWORD</code> nel file <code>.env</code> del backend).
          </div>
        </div>
      ) : em?.servizio_attivo === false ? (
        <div className="eden-card" style={{ padding: '14px 18px', marginBottom: 18, borderLeft: '3px solid var(--red)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          <AlertCircle size={18} color="var(--red)" style={{ marginTop: 2, flexShrink: 0 }} />
          <div style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>
            <b style={{ color: 'var(--red)' }}>Servizio Aruba scaduto — fatturazione elettronica disattivata.</b>
            <div style={{ marginTop: 4, color: 'var(--text-muted)' }}>{em?.servizio_messaggio || 'L\'utenza Aruba risulta scaduta o disabilitata.'}</div>
            <div style={{ marginTop: 4 }}>Rinnova il servizio su Aruba, poi premi “Verifica servizio”. L'invio resta bloccato finché il servizio non torna attivo.</div>
          </div>
          <button onClick={verificaServizio} disabled={verificando} className="eden-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <RefreshCw size={14} /> {verificando ? 'Verifico…' : 'Verifica servizio'}
          </button>
        </div>
      ) : (
        <div className="eden-card" style={{ padding: '10px 16px', marginBottom: 18, borderLeft: '3px solid var(--green)', display: 'flex', gap: 10, alignItems: 'center' }}>
          <CheckCircle2 size={16} color="var(--green)" style={{ flexShrink: 0 }} />
          <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>Servizio Aruba attivo · ambiente <b>{em?.ambiente === 'produzione' ? 'produzione' : 'demo'}</b>.</span>
          <button onClick={verificaServizio} disabled={verificando} style={{ background: 'var(--bg3)', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: 'var(--text)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
            <RefreshCw size={13} /> {verificando ? 'Verifico…' : 'Verifica servizio'}
          </button>
        </div>
      )}

      {loading ? <ListSkeleton rows={6} /> : (
        <>
          {/* ── Emittente / config ── */}
          <div className="eden-card" style={{ padding: 20, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: editEm ? 18 : 6 }}>
              <Building2 size={18} color="var(--cyan)" />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text)', flex: 1 }}>Dati emittente (cedente/prestatore)</h3>
              {!editEm && (
                <button onClick={() => { setEmForm(em); setEditEm(true); }} className="eden-btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                  <Settings size={14} /> Configura
                </button>
              )}
            </div>

            {!editEm ? (
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {em?.denominazione || (em?.nome ? `${em.nome} ${em.cognome || ''}` : null)
                  ? <>
                      <span style={{ color: 'var(--text)', fontWeight: 600 }}>{em.denominazione || `${em.nome} ${em.cognome || ''}`}</span>
                      {em.piva ? ` · P.IVA ${em.piva}` : ''} · Regime {em.regime_fiscale}
                      {em.natura_iva ? ` · Natura ${em.natura_iva}` : ` · IVA ${em.aliquota_iva}%`}
                    </>
                  : <span>Nessun dato emittente configurato.</span>}
                {emMissing.length > 0 && (
                  <div style={{ marginTop: 8, color: 'var(--yellow)', fontSize: 12 }}>
                    Mancano: {emMissing.join(', ')}
                  </div>
                )}
                {emMissing.length === 0 && em?.denominazione && (
                  <div style={{ marginTop: 8, color: 'var(--green)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <CheckCircle2 size={14} /> Emittente completo
                  </div>
                )}
              </div>
            ) : (
              <div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 14 }}>
                  <Field label="Denominazione"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.denominazione || ''} onChange={e => setEmForm(f => ({ ...f, denominazione: e.target.value }))} placeholder="Ragione sociale" /></Field>
                  <Field label="P.IVA"><input className="eden-input" maxLength={28} style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.piva || ''} onChange={e => setEmForm(f => ({ ...f, piva: e.target.value }))} /></Field>
                  <Field label="Codice Fiscale"><input className="eden-input" maxLength={16} style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.codice_fiscale || ''} onChange={e => setEmForm(f => ({ ...f, codice_fiscale: e.target.value }))} placeholder="11 (società) o 16 (persona)" /></Field>
                  <Field label="Regime fiscale">
                    <div style={{ position: 'relative' }}>
                      <select className="eden-select" style={{ width: '100%', paddingRight: 32 }} value={emForm.regime_fiscale || 'RF01'} onChange={e => {
                        const rf = e.target.value;
                        setEmForm(f => rf === 'RF19'
                          ? { ...f, regime_fiscale: rf, natura_iva: f.natura_iva || 'N2.2', aliquota_iva: 0, applica_bollo: true }
                          : { ...f, regime_fiscale: rf });
                      }}>
                        {REGIMI.map(([k, v]) => <option key={k} value={k}>{k} — {v}</option>)}
                      </select>
                      <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                    </div>
                  </Field>
                  <Field label="Aliquota IVA % (ordinario)"><input className="eden-input" type="number" step="0.01" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.aliquota_iva ?? 22} onChange={e => setEmForm(f => ({ ...f, aliquota_iva: e.target.value }))} /></Field>
                  <Field label="Natura IVA (se esente/forfettario)"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.natura_iva || ''} onChange={e => setEmForm(f => ({ ...f, natura_iva: e.target.value }))} placeholder="es. N2.2" /></Field>
                  <Field label="Indirizzo sede"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.sede_via || ''} onChange={e => setEmForm(f => ({ ...f, sede_via: e.target.value }))} /></Field>
                  <Field label="CAP"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.sede_cap || ''} onChange={e => setEmForm(f => ({ ...f, sede_cap: e.target.value }))} /></Field>
                  <Field label="Comune"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.sede_comune || ''} onChange={e => setEmForm(f => ({ ...f, sede_comune: e.target.value }))} /></Field>
                  <Field label="Provincia (sigla)"><input className="eden-input" maxLength={2} style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.sede_provincia || ''} onChange={e => setEmForm(f => ({ ...f, sede_provincia: e.target.value.toUpperCase() }))} /></Field>
                  <Field label="IBAN (opzionale)"><input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={emForm.iban || ''} onChange={e => setEmForm(f => ({ ...f, iban: e.target.value }))} /></Field>
                  <Field label="Ambiente">
                    <div style={{ position: 'relative' }}>
                      <select className="eden-select" style={{ width: '100%', paddingRight: 32 }} value={emForm.ambiente || 'demo'} onChange={e => setEmForm(f => ({ ...f, ambiente: e.target.value }))}>
                        <option value="demo">Demo (test)</option>
                        <option value="produzione">Produzione (invii reali)</option>
                      </select>
                      <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
                    </div>
                  </Field>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text)', marginBottom: 16 }}>
                  <input type="checkbox" checked={!!emForm.applica_bollo} onChange={e => setEmForm(f => ({ ...f, applica_bollo: e.target.checked }))} />
                  Applica bollo €2 (tipico forfettario per importi &gt; €77,47)
                </label>
                <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setEditEm(false); setEmForm(em); }} style={{ background: 'var(--bg3)', color: 'var(--text-muted)', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}>Annulla</button>
                  <button onClick={saveEmittente} disabled={savingEm} className="eden-btn-primary">{savingEm ? 'Salvataggio…' : 'Salva emittente'}</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Stats ── */}
          {stats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
              <StatCard icon={Clock} color="var(--text-muted)" label="Bozze" value={stats.bozze} />
              <StatCard icon={Send} color="var(--cyan)" label="Inviate" value={stats.inviate} />
              <StatCard icon={AlertCircle} color="var(--red)" label="Da correggere" value={stats.problemi} />
              <StatCard icon={Euro} color="var(--green)" label="Fatturato" value={formatEuro(stats.fatturato)} />
            </div>
          )}

          {/* ── Nuova fattura ── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button className="eden-btn-primary" onClick={() => setShowNew(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Plus size={16} /> Nuova fattura elettronica
            </button>
          </div>

          {showNew && (
            <NuovaFattura
              clientiList={clientiList}
              emMissing={emMissing}
              onCancel={() => setShowNew(false)}
              onClientUpdated={(cid, patch) => setClientiList(list => list.map(c => c.id === cid ? { ...c, ...patch } : c))}
              onCreated={() => { setShowNew(false); reloadDocs(); success('Bozza creata'); }}
            />
          )}

          {/* ── Elenco documenti ── */}
          {grouped.length === 0 ? (
            <EmptyState icon={Receipt} title="Nessuna fattura elettronica" hint="Crea la prima bozza dai clienti attivi." />
          ) : grouped.map(({ anno, items }) => (
            <div key={anno} style={{ marginBottom: 28 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <span style={{ fontWeight: 800, fontSize: 18, color: 'var(--text)' }}>{anno}</span>
                <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(d => {
                  const si = statoInfo(d.stato);
                  const busy = busyId === d.id;
                  return (
                    <div key={d.id} className="eden-card" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', flexWrap: 'wrap' }}>
                      <div style={{ width: 70, fontWeight: 800, color: 'var(--text)' }}>#{d.numero}</div>
                      <div style={{ flex: 1, minWidth: 160 }}>
                        <div style={{ fontWeight: 600, color: 'var(--text)', fontSize: 14 }}>{d.azienda || d.cliente_nome || '—'}</div>
                        <div style={{ color: 'var(--text-dim)', fontSize: 12 }}>{d.data_documento} · {d.tipo_documento}</div>
                      </div>
                      <div style={{ fontWeight: 700, color: 'var(--text)', minWidth: 90, textAlign: 'right' }}>{formatEuro(d.totale)}</div>
                      <span style={{ borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, color: si.c, background: si.bg }}>{si.l}</span>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <IconBtn title="Anteprima XML" onClick={() => apriXml(d)}><Eye size={15} /></IconBtn>
                        <IconBtn title="Scarica XML" onClick={() => scaricaXml(d)}><Download size={15} /></IconBtn>
                        <IconBtn title="PDF stampabile (copia di cortesia)" onClick={() => scaricaPdf(d)} disabled={busy}><Printer size={15} /></IconBtn>
                        {puoInviare(d.stato) && (
                          <IconBtn title={em?.fe_attiva ? 'Invia a SDI' : 'Invio non disponibile: servizio Aruba non attivo'} onClick={() => inviaDoc(d)} disabled={busy || !em?.fe_attiva} color="var(--cyan)"><Send size={15} /></IconBtn>
                        )}
                        {!isBozza(d.stato) && (
                          <IconBtn title="Aggiorna stato da SDI" onClick={() => aggiornaStato(d)} disabled={busy}><RefreshCw size={15} /></IconBtn>
                        )}
                        {isBozza(d.stato) && (
                          <IconBtn title="Elimina bozza" onClick={() => eliminaDoc(d)} disabled={busy} color="var(--red)"><Trash2 size={15} /></IconBtn>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {/* ── Modal XML ── */}
      {xmlModal && (
        <div onClick={() => setXmlModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div onClick={e => e.stopPropagation()} className="eden-card" style={{ width: 'min(820px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <Receipt size={16} color="var(--cyan)" />
              <span style={{ fontWeight: 700, color: 'var(--text)', flex: 1, fontSize: 14 }}>{xmlModal.nome || 'Anteprima XML'}</span>
              <button onClick={() => downloadFile(xmlModal.text, xmlModal.nome || 'fattura.xml')} style={{ background: 'var(--cyan-dim)', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: 'var(--cyan)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}><Download size={13} /> Scarica</button>
              <button onClick={() => { navigator.clipboard?.writeText(xmlModal.text); success('XML copiato'); }} style={{ background: 'var(--bg3)', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', color: 'var(--text)', fontSize: 12 }}>Copia</button>
              <button onClick={() => setXmlModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={18} /></button>
            </div>
            <pre style={{ margin: 0, padding: 18, overflow: 'auto', fontSize: 12, lineHeight: 1.5, color: 'var(--text)', background: 'var(--bg2)', whiteSpace: 'pre' }}>{xmlModal.text}</pre>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, color, label, value }) {
  return (
    <div className="eden-card" style={{ padding: '16px 18px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Icon size={15} color={color} />
        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{label}</span>
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

function IconBtn({ children, title, onClick, disabled, color = 'var(--text-dim)' }) {
  return (
    <button title={title} onClick={onClick} disabled={disabled}
      style={{ background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', color, padding: 6, borderRadius: 6, display: 'flex', alignItems: 'center', opacity: disabled ? 0.4 : 1 }}>
      {children}
    </button>
  );
}

// ── Form nuova fattura: seleziona cliente, completa dati fiscali, righe ──
function NuovaFattura({ clientiList, emMissing, onCancel, onCreated, onClientUpdated }) {
  const { success } = useToast();
  const [clienteId, setClienteId] = useState('');
  const [causale, setCausale] = useState('');
  const [dataDoc, setDataDoc] = useState(new Date().toISOString().slice(0, 10));
  const [righe, setRighe] = useState([{ descrizione: '', quantita: 1, prezzo_unitario: '' }]);
  const [fiscalForm, setFiscalForm] = useState(null);
  const [savingFiscal, setSavingFiscal] = useState(false);
  const [creating, setCreating] = useState(false);

  const cliente = clientiList.find(c => c.id === clienteId);

  useEffect(() => {
    if (cliente) {
      setFiscalForm({
        piva: cliente.piva || '', codice_fiscale: cliente.codice_fiscale || '',
        codice_destinatario: cliente.codice_destinatario || '', pec: cliente.pec || '',
        indirizzo_via: cliente.indirizzo_via || '', indirizzo_cap: cliente.indirizzo_cap || '',
        indirizzo_comune: cliente.indirizzo_comune || '', indirizzo_provincia: cliente.indirizzo_provincia || '',
        indirizzo_nazione: cliente.indirizzo_nazione || 'IT', tipo_soggetto: cliente.tipo_soggetto || 'privato',
      });
      if (!causale && cliente.valore_contratto) setCausale('Canone servizi');
      if (righe.length === 1 && !righe[0].descrizione && cliente.valore_contratto) {
        setRighe([{ descrizione: 'Servizi di gestione social media', quantita: 1, prezzo_unitario: cliente.valore_contratto }]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const missingCliente = useMemo(() => {
    if (!fiscalForm) return [];
    const m = [];
    if (!fiscalForm.piva && !fiscalForm.codice_fiscale) m.push('P.IVA o Codice Fiscale');
    if (!fiscalForm.codice_destinatario && !fiscalForm.pec) m.push('Codice Destinatario o PEC');
    ['indirizzo_via', 'indirizzo_cap', 'indirizzo_comune', 'indirizzo_provincia'].forEach(f => {
      if (!fiscalForm[f]) m.push(CLIENTE_FISCAL.find(x => x[0] === f)[1]);
    });
    return m;
  }, [fiscalForm]);

  const salvaFiscal = async () => {
    setSavingFiscal(true);
    try {
      await Clienti.update(clienteId, fiscalForm);
      onClientUpdated(clienteId, fiscalForm);
      success('Dati fiscali cliente salvati');
    } catch (_) {}
    setSavingFiscal(false);
  };

  const totale = useMemo(() => righe.reduce((s, r) => s + (Number(r.quantita) || 0) * (Number(r.prezzo_unitario) || 0), 0), [righe]);

  const setRiga = (i, patch) => setRighe(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r));
  const addRiga = () => setRighe(rs => [...rs, { descrizione: '', quantita: 1, prezzo_unitario: '' }]);
  const delRiga = (i) => setRighe(rs => rs.filter((_, j) => j !== i));

  const crea = async () => {
    setCreating(true);
    try {
      await FE.create({
        cliente_id: clienteId, causale, data: dataDoc,
        righe: righe.map(r => ({ descrizione: r.descrizione, quantita: Number(r.quantita) || 1, prezzo_unitario: Number(r.prezzo_unitario) || 0 })),
      });
      onCreated();
    } catch (_) {} finally { setCreating(false); }
  };

  const righeValide = righe.length > 0 && righe.every(r => r.descrizione && Number(r.prezzo_unitario) > 0);
  const canCreate = clienteId && righeValide && missingCliente.length === 0 && emMissing.length === 0;

  return (
    <div className="eden-card" style={{ padding: 22, marginBottom: 22 }}>
      <h3 style={{ margin: '0 0 18px', color: 'var(--text)', fontSize: 16, fontWeight: 700 }}>Nuova fattura elettronica</h3>

      {emMissing.length > 0 && (
        <div style={{ marginBottom: 16, padding: '10px 14px', borderRadius: 8, background: 'var(--yellow-dim)', color: 'var(--yellow)', fontSize: 12 }}>
          Completa prima i <b>dati emittente</b>: {emMissing.join(', ')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 14, marginBottom: 16 }}>
        <Field label="Cliente">
          <div style={{ position: 'relative' }}>
            <select className="eden-select" style={{ width: '100%', paddingRight: 32 }} value={clienteId} onChange={e => setClienteId(e.target.value)}>
              <option value="">Seleziona cliente attivo…</option>
              {clientiList.map(c => <option key={c.id} value={c.id}>{c.azienda || c.nome}</option>)}
            </select>
            <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
          </div>
        </Field>
        <Field label="Data documento">
          <input className="eden-input" type="date" style={{ width: '100%', boxSizing: 'border-box' }} value={dataDoc} onChange={e => setDataDoc(e.target.value)} />
        </Field>
      </div>

      {/* Dati fiscali cliente inline */}
      {cliente && fiscalForm && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', flex: 1 }}>Dati fiscali cliente</span>
            {missingCliente.length === 0
              ? <span style={{ color: 'var(--green)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}><CheckCircle2 size={14} /> Completi</span>
              : <span style={{ color: 'var(--yellow)', fontSize: 12 }}>Mancano: {missingCliente.join(', ')}</span>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {CLIENTE_FISCAL.map(([key, label]) => (
              <Field key={key} label={label}>
                <input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }}
                  maxLength={key === 'indirizzo_provincia' ? 2 : (key === 'codice_destinatario' ? 7 : undefined)}
                  value={fiscalForm[key] || ''}
                  onChange={e => setFiscalForm(f => ({ ...f, [key]: key === 'indirizzo_provincia' ? e.target.value.toUpperCase() : e.target.value }))} />
              </Field>
            ))}
            <Field label="Tipo soggetto">
              <div style={{ position: 'relative' }}>
                <select className="eden-select" style={{ width: '100%', paddingRight: 32 }} value={fiscalForm.tipo_soggetto} onChange={e => setFiscalForm(f => ({ ...f, tipo_soggetto: e.target.value }))}>
                  <option value="privato">Privato / Azienda</option>
                  <option value="PA">Pubblica Amministrazione</option>
                </select>
                <ChevronDown size={14} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', color: 'var(--text-muted)' }} />
              </div>
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button onClick={salvaFiscal} disabled={savingFiscal} style={{ background: 'var(--bg3)', color: 'var(--text)', border: 'none', borderRadius: 8, padding: '7px 16px', cursor: 'pointer', fontSize: 13 }}>
              {savingFiscal ? 'Salvataggio…' : 'Salva dati fiscali'}
            </button>
          </div>
        </div>
      )}

      {/* Righe */}
      <Label>Righe fattura</Label>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 8 }}>
        {righe.map((r, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input className="eden-input" style={{ flex: 1 }} placeholder="Descrizione" value={r.descrizione} onChange={e => setRiga(i, { descrizione: e.target.value })} />
            <input className="eden-input" style={{ width: 70 }} type="number" step="0.01" min="0" placeholder="Q.tà" value={r.quantita} onChange={e => setRiga(i, { quantita: e.target.value })} />
            <input className="eden-input" style={{ width: 110 }} type="number" step="0.01" min="0" placeholder="Prezzo €" value={r.prezzo_unitario} onChange={e => setRiga(i, { prezzo_unitario: e.target.value })} />
            {righe.length > 1 && <IconBtn title="Rimuovi riga" onClick={() => delRiga(i)} color="var(--red)"><Trash2 size={15} /></IconBtn>}
          </div>
        ))}
      </div>
      <button onClick={addRiga} style={{ background: 'none', border: '1px dashed var(--border)', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16 }}>
        <Plus size={14} /> Aggiungi riga
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, alignItems: 'end', marginBottom: 18 }}>
        <Field label="Causale (opzionale)">
          <input className="eden-input" style={{ width: '100%', boxSizing: 'border-box' }} value={causale} onChange={e => setCausale(e.target.value)} placeholder="es. Canone servizi settembre 2026" />
        </Field>
        <div style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 13 }}>
          Imponibile<br /><span style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>{formatEuro(totale)}</span>
          <div style={{ fontSize: 11 }}>+ IVA/bollo secondo regime</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
        <button onClick={onCancel} style={{ background: 'var(--bg3)', color: 'var(--text-muted)', border: 'none', borderRadius: 8, padding: '8px 18px', cursor: 'pointer', fontSize: 14 }}>Annulla</button>
        <button onClick={crea} disabled={!canCreate || creating} className="eden-btn-primary" style={{ opacity: (!canCreate || creating) ? 0.5 : 1 }}>
          {creating ? 'Creazione…' : 'Crea bozza'}
        </button>
      </div>
    </div>
  );
}
